import { useState } from "react";
import { AlertTriangle, Check, ChevronDown, ChevronRight, Globe, Loader2, RotateCcw, Sparkles, X } from "lucide-react";
import { assetUrl } from "@/lib/api";

const usd = (n) => `≈ $${(n || 0) < 0.01 && n > 0 ? (n || 0).toFixed(3) : (n || 0).toFixed(2)}`;

const StepIcon = ({ state }) => (state === "done" ? <Check className="h-3.5 w-3.5 text-emerald-300" />
  : state === "failed" ? <X className="h-3.5 w-3.5 text-red-300" /> : state === "stopped" ? <span className="h-2.5 w-2.5 rounded-sm bg-slate-400" />
  : <Loader2 className="h-3.5 w-3.5 animate-spin text-cyan-300" />);

export const StepsCard = ({ steps, cost, live, testId }) => {
  const [open, setOpen] = useState(!!live);
  const shown = steps.filter((s) => s.label !== "Starting" || live);
  const running = shown.find((s) => s.state === "running");
  const failed = shown.some((s) => s.state === "failed");
  const stopped = shown.some((s) => s.state === "stopped");
  return (
    <div data-testid={testId} className="rounded-xl border border-white/10 bg-white/[0.02] text-sm">
      <button onClick={() => setOpen(!open)} data-testid={`${testId}-toggle`} className="flex w-full items-center gap-2 px-3 py-2 text-left text-slate-200">
        {open ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
        <StepIcon state={running ? "running" : failed ? "failed" : stopped ? "stopped" : "done"} />
        <span className="flex-1 truncate">{running ? running.label : failed ? "A step failed" : stopped ? "Stopped" : `${shown.length} step${shown.length === 1 ? "" : "s"} done`}</span>
        <span data-testid={`${testId}-cost`} className="font-mono-tech text-[11px] text-slate-500">{usd(cost)}</span>
      </button>
      {open && (
        <ul className="space-y-1 border-t border-white/5 px-3 py-2">
          {shown.map((s) => (
            <li key={s.label} data-testid={`step-${s.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} data-state={s.state} className="flex items-center gap-2 text-xs text-slate-300">
              <StepIcon state={s.state} /><span className="flex-1">{s.label}</span><span className="font-mono-tech text-[11px] text-slate-500">{s.cost ? usd(s.cost) : ""}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Questions = ({ m, onAnswer, disabled }) => {
  const qs = m.data?.questions || [];
  const [ans, setAns] = useState({});
  const [free, setFree] = useState("");
  const toggle = (q, o) => setAns((a) => {
    const cur = a[q.id] || [];
    return { ...a, [q.id]: q.multi ? (cur.includes(o) ? cur.filter((x) => x !== o) : [...cur, o]) : [o] };
  });
  const submit = () => {
    const lines = qs.filter((q) => ans[q.id]?.length).map((q) => `${q.question} — ${ans[q.id].join(", ")}`);
    onAnswer([...lines, free.trim()].filter(Boolean).join("\n") || "Just build it with sensible defaults");
  };
  return (
    <div data-testid={`questions-card-${m.id}`} className="space-y-3 rounded-xl border border-cyan-400/20 bg-cyan-400/[0.03] p-3">
      {qs.map((q, i) => (
        <div key={q.id || i}>
          <p className="mb-1.5 text-sm text-white">{i + 1}. {q.question}</p>
          <div className="flex flex-wrap gap-1.5">
            {(q.options || []).map((o) => (
              <button key={o} disabled={disabled} onClick={() => toggle(q, o)} data-testid={`question-option-${i}-${o.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${(ans[q.id] || []).includes(o) ? "border-cyan-400 bg-cyan-400/15 text-cyan-100" : "border-white/10 text-slate-300 hover:border-cyan-400/40"}`}>{o}</button>
            ))}
          </div>
        </div>
      ))}
      <input value={free} onChange={(e) => setFree(e.target.value)} disabled={disabled} placeholder="Anything else? (optional)" data-testid={`questions-free-${m.id}`}
        className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-1.5 text-sm text-white outline-none" />
      <div className="flex gap-2">
        <button onClick={submit} disabled={disabled} data-testid={`questions-submit-${m.id}`} className="rounded-full bg-cyan-400 px-4 py-1.5 text-xs font-semibold text-[#07090E] disabled:opacity-50">Send answers</button>
        <button onClick={() => onAnswer("Just build it — use sensible defaults")} disabled={disabled} data-testid={`questions-skip-${m.id}`} className="rounded-full border border-white/10 px-4 py-1.5 text-xs text-slate-300 disabled:opacity-50">Just build it</button>
      </div>
    </div>
  );
};

const PlanCard = ({ m, onApprove, onChange, disabled, latest }) => {
  const { plan, app_mode: app, defaults = [] } = m.data || {};
  const dm = plan?.data_model;
  const feats = Object.entries(dm?.features || {}).filter(([, v]) => v).map(([k]) => k);
  return (
    <div data-testid={`plan-card-${m.id}`} className="space-y-3 rounded-xl border border-white/10 bg-white/[0.02] p-3 text-sm">
      {plan?.tagline && <p className="text-slate-300">“{plan.tagline}”</p>}
      <ul className="space-y-1.5">
        {(plan?.pages || []).map((p) => (
          <li key={p.file} className="text-slate-200"><span className="font-semibold text-white">{p.title}</span> <span className="font-mono-tech text-[11px] text-slate-500">{p.file}</span>
            <p className="text-xs text-slate-400">{p.sections.map((s) => s.name).join(" · ")}</p></li>
        ))}
      </ul>
      {app && (
        <div data-testid={`plan-data-model-${m.id}`} className="rounded-lg border border-cyan-400/20 p-2 text-xs text-slate-300">
          <p className="mb-1 font-semibold text-cyan-200">App mode{feats.length ? ` · ${feats.join(", ")}` : ""}</p>
          {(dm?.collections || []).map((c) => <p key={c.name}>{c.label}: {c.fields.map((f) => f.label).join(", ")}</p>)}
        </div>
      )}
      {defaults.length > 0 && <div data-testid={`plan-defaults-${m.id}`} className="text-xs text-slate-400"><p className="font-semibold text-slate-300">Defaults I used</p><ul className="list-disc pl-4">{defaults.map((d) => <li key={d}>{d}</li>)}</ul></div>}
      {latest && (
        <div className="flex gap-2">
          <button onClick={onApprove} disabled={disabled} data-testid="plan-approve-btn" className="inline-flex items-center gap-1.5 rounded-full bg-cyan-400 px-4 py-1.5 text-xs font-semibold text-[#07090E] disabled:opacity-50"><Sparkles className="h-3.5 w-3.5" />Approve & build</button>
          <button onClick={onChange} disabled={disabled} data-testid="plan-change-btn" className="rounded-full border border-white/10 px-4 py-1.5 text-xs text-slate-300 disabled:opacity-50">Change plan</button>
        </div>
      )}
    </div>
  );
};

export const Message = ({ m, ctx }) => {
  const { running, currentVersion, latestPlanId, latestQuestionsId, hasPlan, onAnswer, onApprove, onChangePlan, onRetry, onRollback, latestErrorId } = ctx;
  if (m.kind === "steps") return <StepsCard steps={m.data?.steps || []} cost={m.data?.cost} testId={`steps-card-${m.id}`} />;
  if (m.role === "note") {
    const failed = m.kind === "reference" && m.data?.failed;
    return (
      <p data-testid={`note-${m.id}`} className={`flex items-start gap-2 text-xs ${failed ? "text-amber-200" : "text-slate-400"}`}>
        {m.kind === "reference" ? <Globe className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-slate-500" />}
        <span dangerouslySetInnerHTML={{ __html: (m.text || "").replace(/[<>&]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" })[c]).replace(/\*\*(.+?)\*\*/g, "<b>$1</b>") }} />
      </p>
    );
  }
  const mine = m.role === "user";
  return (
    <div data-testid={`message-${m.id}`} className={`group flex flex-col gap-2 ${mine ? "items-end" : "items-start"}`}>
      {m.images?.length > 0 && <div className="flex flex-wrap justify-end gap-1.5">{m.images.map((i) => <img key={i.path} src={assetUrl(i.url)} alt={i.name} className="h-20 w-20 rounded-lg border border-white/10 object-cover" />)}</div>}
      {m.text && <div className={`max-w-[92%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-cyan-400/15 text-cyan-50" : m.kind === "error" ? "border border-red-400/30 bg-red-500/10 text-red-100" : "text-slate-100"}`}>
        {m.kind === "error" && <AlertTriangle className="mr-1.5 inline h-4 w-4" />}{m.text}</div>}
      {m.kind === "questions" && <div className="w-full"><Questions m={m} onAnswer={onAnswer} disabled={running || m.id !== latestQuestionsId || hasPlan || !!currentVersion} /></div>}
      {m.kind === "plan" && <div className="w-full"><PlanCard m={m} latest={m.id === latestPlanId && !currentVersion} onApprove={onApprove} onChange={onChangePlan} disabled={running} /></div>}
      {m.kind === "error" && m.id === latestErrorId && <button onClick={onRetry} disabled={running} data-testid="chat-retry-btn" className="inline-flex items-center gap-1.5 rounded-full border border-red-400/40 px-3 py-1 text-xs text-red-200 disabled:opacity-50"><RotateCcw className="h-3.5 w-3.5" />Retry</button>}
      {m.version && m.version !== currentVersion && (
        <button onClick={() => onRollback(m.version)} disabled={running} data-testid={`rollback-${m.id}`} className="text-[11px] text-slate-500 opacity-80 hover:text-cyan-200 group-hover:opacity-100">↺ Rollback to here (v{m.version})</button>
      )}
    </div>
  );
};
